import { getAuthHeaders } from './authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

class AdminApiService {
  // ========== TENANT MANAGEMENT ==========

  async getTenants(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.environment) params.append('environment', filters.environment);
      if (filters.provider) params.append('provider', filters.provider);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await fetch(`${API_BASE_URL}/api/admin/tenants?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getTenant(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${id}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createTenant(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateTenant(id, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteTenant(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== USER MANAGEMENT ==========

  async getUsers(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.role) params.append('role', filters.role);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await fetch(`${API_BASE_URL}/api/admin/users?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getUser(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createUser(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateUser(id, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteUser(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== USER-TENANT ACCESS MANAGEMENT ==========

  async grantTenantAccess(userId, tenantId, role = 'user') {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/tenants`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, role })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async revokeTenantAccess(userId, tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}/tenants/${tenantId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== TEMPLATE & PROVISIONING ==========

  async getTemplates(sourceProvider, destination) {
    try {
      const params = new URLSearchParams();
      params.append('sourceProvider', sourceProvider);
      params.append('destination', destination);

      const response = await fetch(`${API_BASE_URL}/api/admin/templates?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getProvisionedServices(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/provisioned`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getUnprovisionedTemplates(tenantId, searchQuery = '') {
    try {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/unprovisioned/${tenantId}?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async provisionServices(tenantId, services) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/provision`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ services })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getCloneableServices(sourceTenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${sourceTenantId}/cloneable-services`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async copyServicesFromTenant(targetTenantId, payload) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${targetTenantId}/copy-services`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== EXPORT/IMPORT ==========

  async exportTenantConfigurations(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/export`, {
        method: 'GET',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `tenant-${tenantId}-${Date.now()}.cartnxs`;

      // Get blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return { success: true, filename };
    } catch (error) {
      throw error;
    }
  }

  async importTenantConfigurations(tenantId, file, strategy = 'replace') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('strategy', strategy);

      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/import`, {
        method: 'POST',
        headers: getAuthHeaders({}, true), // Skip Content-Type for FormData
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== ANALYTICS ==========

  async getAdminAnalytics(timeRange = '7d', filters = {}) {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      if (filters.service) params.append('service', filters.service);
      if (filters.tenantId) params.append('tenantId', filters.tenantId);

      const response = await fetch(`${API_BASE_URL}/api/admin/analytics?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== HEALTH MONITORING ==========

  async getHealthStatus() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/health`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getTenantHealth(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/health/${tenantId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== FLOW TEMPLATE MANAGEMENT ==========

  async getFlowTemplates(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.sourceProvider) params.append('sourceProvider', filters.sourceProvider);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.status) params.append('status', filters.status);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getFlowTemplate(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates/${id}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createFlowTemplate(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateFlowTemplate(id, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteFlowTemplate(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async extractFlowTemplate(tenantId, key, sourceProvider, destination) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/flow-templates/extract`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, key, sourceProvider, destination })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // ========== DTO TEMPLATE MANAGEMENT ==========

  async getDtoTemplates(filters = {}) {
    try {
      const params = new URLSearchParams();
      if (filters.sourceProvider) params.append('sourceProvider', filters.sourceProvider);
      if (filters.destination) params.append('destination', filters.destination);
      if (filters.status) params.append('status', filters.status);
      if (filters.key) params.append('key', filters.key);
      if (filters.page) params.append('page', filters.page);
      if (filters.limit) params.append('limit', filters.limit);

      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates?${params.toString()}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getDtoTemplate(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/${id}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createDtoTemplate(data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateDtoTemplate(id, data) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/${id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async deleteDtoTemplate(id) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async extractDtoTemplate(tenantId, key) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/extract`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId, key })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async extractAllDtoTemplates(tenantId) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/dto-templates/extract-all`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenantId })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export const adminApi = new AdminApiService();
